/**
 * Seo — per-page, locale-aware document head.
 *
 * React 19 hoists <title>, <meta> and <link> rendered anywhere in the tree into
 * <head>, so this component can live inside any page. It produces:
 *   - a localized <title> and meta description (+ Open Graph / Twitter)
 *   - <html>-level og:locale reflecting the active language
 *   - hreflang alternates for every supported language (multilingual SEO),
 *     using `?lng=<code>` URLs so locales are shareable and future-scalable.
 *
 * Usage:
 *   <Seo titleKey="industryPages.oilGas.seo.title"
 *        descriptionKey="industryPages.oilGas.seo.description" />
 *
 * @format
 */

import { useTranslation } from "react-i18next";

import { LANGUAGES, getLanguageMeta } from "@/i18n/config";

interface SeoProps {
	/** Translation key for the page title. */
	titleKey?: string;
	/** Translation key for the meta description. */
	descriptionKey?: string;
	/** Literal title (overrides titleKey). */
	title?: string;
	/** Literal description (overrides descriptionKey). */
	description?: string;
	/** Path the hreflang alternates point at. Defaults to the current path. */
	path?: string;
}

const SITE_NAME = "Positron";

export function Seo({
	titleKey,
	descriptionKey,
	title,
	description,
	path,
}: SeoProps) {
	const { t, i18n } = useTranslation();

	const resolvedTitle = title ?? (titleKey ? t(titleKey) : SITE_NAME);
	const resolvedDescription =
		description ?? (descriptionKey ? t(descriptionKey) : undefined);

	const origin =
		typeof window !== "undefined" ? window.location.origin : "";
	const basePath =
		path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
	const ogLocale = getLanguageMeta(i18n.language).code;

	const buildUrl = (lng?: string) =>
		`${origin}${basePath}${lng ? `?lng=${lng}` : ""}`;

	return (
		<>
			<title>{resolvedTitle}</title>
			{resolvedDescription && (
				<meta
					name="description"
					content={resolvedDescription}
				/>
			)}

			{/* Open Graph */}
			<meta
				property="og:title"
				content={resolvedTitle}
			/>
			{resolvedDescription && (
				<meta
					property="og:description"
					content={resolvedDescription}
				/>
			)}
			<meta
				property="og:type"
				content="website"
			/>
			<meta
				property="og:site_name"
				content={SITE_NAME}
			/>
			<meta
				property="og:locale"
				content={ogLocale}
			/>

			{/* Twitter */}
			<meta
				name="twitter:card"
				content="summary_large_image"
			/>
			<meta
				name="twitter:title"
				content={resolvedTitle}
			/>
			{resolvedDescription && (
				<meta
					name="twitter:description"
					content={resolvedDescription}
				/>
			)}

			{/* hreflang alternates for every supported language + x-default */}
			{LANGUAGES.map((lang) => (
				<link
					key={lang.code}
					rel="alternate"
					hrefLang={lang.code}
					href={buildUrl(lang.code)}
				/>
			))}
			<link
				rel="alternate"
				hrefLang="x-default"
				href={buildUrl()}
			/>
		</>
	);
}

export default Seo;
